import { useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { AlertCircle, ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react"
import { toast } from "sonner"

import { cn } from "@/lib/utils"
import { authApi, AuthApiError } from "@/lib/auth-api"
import { clearStoredReturnUrl, readStoredReturnUrl, useAuth } from "@/providers/auth-provider"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { TurnstileWidget } from "@/components/shared/turnstile-widget"

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(1, "Password is required"),
})

const signupSchema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(8, "Use at least 8 characters"),
  client_number: z.string().min(1, "Client code is required"),
})

type LoginFormValues = z.infer<typeof loginSchema>
type SignupFormValues = z.infer<typeof signupSchema>

function GoogleIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path
        d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
        fill="currentColor"
      />
    </svg>
  )
}

function AppleIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path
        d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
        fill="currentColor"
      />
    </svg>
  )
}

function OAuthButton({
  label,
  provider,
  returnTo,
  icon,
}: {
  label: string
  provider: "google" | "apple"
  returnTo: string | null
  icon: React.ReactNode
}) {
  const [isStarting, setIsStarting] = useState(false)

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      onClick={() => {
        setIsStarting(true)
        window.location.href = authApi.getOAuthUrl(provider, returnTo ?? undefined)
      }}
      disabled={isStarting}
    >
      {icon}
      {label}
      <ArrowRight className="ml-auto" />
    </Button>
  )
}

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const { login, isAuthenticated, user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get("returnTo")
  const storedReturnTo = readStoredReturnUrl()
  const oauthError = searchParams.get("oauth_error")
  const isExpired = searchParams.get("expired") === "1"
  const initialTab = searchParams.get("mode") === "signup" ? "signup" : "login"
  const [activeTab, setActiveTab] = useState(initialTab)
  const [showPassword, setShowPassword] = useState(false)
  const [showSignupPassword, setShowSignupPassword] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [signupError, setSignupError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSignupLoading, setIsSignupLoading] = useState(false)
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  })

  const signupForm = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: { name: "", email: "", password: "", client_number: "" },
  })

  useEffect(() => {
    if (isAuthenticated && user) {
      const expired = searchParams.get("expired")
      if (expired) return
      const destination = returnTo ?? storedReturnTo ?? (user.type === "admin" ? "/admin" : "/portal")
      clearStoredReturnUrl()
      navigate(destination, { replace: true })
    }
  }, [isAuthenticated, navigate, returnTo, searchParams, storedReturnTo, user])

  useEffect(() => {
    if (!oauthError) return

    if (oauthError === "google_not_configured" || oauthError === "apple_not_configured") {
      toast.info("OAuth is not configured in this environment yet. Email sign-in is still available.")
      return
    }

    toast.error("OAuth sign-in could not be completed.")
  }, [oauthError])

  async function handleLogin(values: LoginFormValues) {
    setLoginError(null)

    if (!turnstileToken) {
      setLoginError("Complete the security check before signing in.")
      return
    }

    setIsLoading(true)
    try {
      const { token } = await authApi.login(values.email, values.password, turnstileToken)
      login(token)
    } catch (error) {
      const message = error instanceof AuthApiError ? error.message : "Login failed. Please try again."
      setLoginError(message)
      toast.error(message)
      setTurnstileToken(null)
    } finally {
      setIsLoading(false)
    }
  }

  async function handleSignup(values: SignupFormValues) {
    setSignupError(null)
    setIsSignupLoading(true)
    try {
      const result = await authApi.signup(values)
      if (result.token) {
        login(result.token)
        return
      }

      toast.success(result.message ?? "Account created. Please sign in to continue.")
      setActiveTab("login")
      loginForm.setValue("email", values.email)
    } catch (error) {
      const message = error instanceof AuthApiError ? error.message : "Signup failed. Please try again."
      setSignupError(message)
      toast.error(message)
    } finally {
      setIsSignupLoading(false)
    }
  }

  return (
    <div className={cn("flex h-full flex-col gap-6", className)} {...props}>
      <Card className="flex min-h-[46rem] flex-1 flex-col border-border/70 bg-card/95 shadow-xl shadow-primary/5 backdrop-blur">
        <CardHeader className="text-center">
          <CardTitle className="flex justify-center">
            <img src="/branding/360dashwithboxsmall.svg" alt="360Dash" className="h-10 w-auto" />
          </CardTitle>
          <CardDescription>
            Sign in to your dashboard or create your account with the client code you were given.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="grid gap-6">
            {(isExpired || oauthError) && (
              <Alert className="border-primary/20 bg-primary/5 [&>svg]:text-primary">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{isExpired ? "Session expired" : "Sign-in notice"}</AlertTitle>
                <AlertDescription>
                  {isExpired
                    ? "Your session ended, so we brought you back here safely. Sign in again to continue."
                    : "A social sign-in attempt could not be completed in this environment. Email login still works."}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col gap-4">
              <OAuthButton
                label="Login with Apple"
                provider="apple"
                returnTo={returnTo}
                icon={<AppleIcon />}
              />
              <OAuthButton
                label="Login with Google"
                provider="google"
                returnTo={returnTo}
                icon={<GoogleIcon />}
              />
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="grid gap-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Login</TabsTrigger>
                <TabsTrigger value="signup">Sign up</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="mt-0">
                <form onSubmit={loginForm.handleSubmit(handleLogin)}>
                  <div className="grid gap-6">
                    <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                      <span className="relative z-10 bg-background px-2 text-muted-foreground">
                        Or continue with email
                      </span>
                    </div>

                    <div className="grid gap-6">
                      <div className="grid gap-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="m@example.com"
                          disabled={isLoading}
                          {...loginForm.register("email")}
                        />
                        {loginForm.formState.errors.email && (
                          <p className="text-xs text-destructive">{loginForm.formState.errors.email.message}</p>
                        )}
                      </div>

                      <div className="grid gap-2">
                        <div className="flex items-center">
                          <Label htmlFor="password">Password</Label>
                          <button
                            type="button"
                            className="ml-auto text-sm underline-offset-4 hover:underline"
                            onClick={() => toast.info("Password reset still flows through dashboard-auth.")}
                          >
                            Forgot your password?
                          </button>
                        </div>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            disabled={isLoading}
                            {...loginForm.register("password")}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((value) => !value)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {loginForm.formState.errors.password && (
                          <p className="text-xs text-destructive">{loginForm.formState.errors.password.message}</p>
                        )}
                      </div>

                      <div className="grid gap-2">
                        <Label>Security check</Label>
                        {turnstileSiteKey ? (
                          <TurnstileWidget siteKey={turnstileSiteKey} onTokenChange={setTurnstileToken} />
                        ) : (
                          <p className="text-xs text-destructive">Turnstile site key is not configured for this build.</p>
                        )}
                        {!turnstileToken && turnstileSiteKey && (
                          <p className="text-xs text-muted-foreground">Complete the Turnstile check to enable login.</p>
                        )}
                      </div>

                      {loginError && (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertTitle>Login failed</AlertTitle>
                          <AlertDescription>{loginError}</AlertDescription>
                        </Alert>
                      )}

                      <Button type="submit" className="w-full" disabled={isLoading || !turnstileToken || !turnstileSiteKey}>
                        {isLoading && <Loader2 className="animate-spin" />}
                        Login
                      </Button>
                    </div>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="signup" className="mt-0">
                <form onSubmit={signupForm.handleSubmit(handleSignup)}>
                  <div className="grid gap-6">
                    <div className="rounded-lg border border-border/70 bg-muted/40 p-4 text-sm text-muted-foreground">
                      Use the client code Steve gave you. This links your new login to the existing project record in D1.
                    </div>

                    <div className="grid gap-6">
                      <div className="grid gap-2">
                        <Label htmlFor="signup-name">Full name</Label>
                        <Input
                          id="signup-name"
                          placeholder="Jane Smith"
                          disabled={isSignupLoading}
                          {...signupForm.register("name")}
                        />
                        {signupForm.formState.errors.name && (
                          <p className="text-xs text-destructive">{signupForm.formState.errors.name.message}</p>
                        )}
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="signup-email">Email</Label>
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="jane@example.com"
                          disabled={isSignupLoading}
                          {...signupForm.register("email")}
                        />
                        {signupForm.formState.errors.email && (
                          <p className="text-xs text-destructive">{signupForm.formState.errors.email.message}</p>
                        )}
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="signup-client-code">Client code</Label>
                        <Input
                          id="signup-client-code"
                          placeholder="S360-12345"
                          disabled={isSignupLoading}
                          {...signupForm.register("client_number")}
                        />
                        {signupForm.formState.errors.client_number && (
                          <p className="text-xs text-destructive">{signupForm.formState.errors.client_number.message}</p>
                        )}
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="signup-password">Password</Label>
                        <div className="relative">
                          <Input
                            id="signup-password"
                            type={showSignupPassword ? "text" : "password"}
                            placeholder="Create a secure password"
                            disabled={isSignupLoading}
                            {...signupForm.register("password")}
                          />
                          <button
                            type="button"
                            onClick={() => setShowSignupPassword((value) => !value)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={showSignupPassword ? "Hide password" : "Show password"}
                          >
                            {showSignupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {signupForm.formState.errors.password && (
                          <p className="text-xs text-destructive">{signupForm.formState.errors.password.message}</p>
                        )}
                      </div>

                      {signupError && (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertTitle>Signup failed</AlertTitle>
                          <AlertDescription>{signupError}</AlertDescription>
                        </Alert>
                      )}

                      <Button type="submit" className="w-full" disabled={isSignupLoading}>
                        {isSignupLoading && <Loader2 className="animate-spin" />}
                        Create account
                      </Button>
                    </div>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-sm leading-6 text-muted-foreground">
        By continuing, you agree to the platform{' '}
        <Link to="/portal/terms" className="font-medium text-foreground underline underline-offset-4">
          terms
        </Link>{' '}
        and{' '}
        <Link to="/portal/privacy" className="font-medium text-foreground underline underline-offset-4">
          privacy
        </Link>{' '}
        expectations for your dashboard account.
      </div>
    </div>
  )
}
